// Address Cache Utility for location-based caching
// Provides smart caching with proximity-based lookup and automatic cleanup

interface CachedAddressData {
  areaName: string;
  areaId: string;
  localityName: string;
  localityId: string;
  existingArea: any | null;
  existingLocalities: any[];
  addressFields: any;
  warehouseId: string;
  timestamp: number;
  coordinates: [number, number];
}

interface CacheEntry {
  key: string;
  data: CachedAddressData;
  distance: number;
}

class AddressCache {
  private readonly DISABLED = process.env.REACT_APP_DISABLE_ADDRESS_CACHE === 'true';
  private readonly CACHE_PREFIX = 'addressCache_';
  private readonly CACHE_EXPIRY_HOURS = 24;
  private readonly PROXIMITY_RADIUS_METERS = 200;

  // Calculate distance between two coordinates using Haversine formula
  private calculateDistance(lat1: number, lng1: number, lat2: number, lng2: number): number {
    const R = 6371e3; // Earth's radius in meters
    const φ1 = lat1 * Math.PI/180;
    const φ2 = lat2 * Math.PI/180;
    const Δφ = (lat2-lat1) * Math.PI/180;
    const Δλ = (lng2-lng1) * Math.PI/180;

    const a = Math.sin(Δφ/2) * Math.sin(Δφ/2) +
            Math.cos(φ1) * Math.cos(φ2) *
            Math.sin(Δλ/2) * Math.sin(Δλ/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));

    return R * c; // Distance in meters
  }

  // Generate cache key from coordinates (rounded to ~100m precision)
  private generateCacheKey(lat: number, lng: number): string {
    const roundedLat = Math.round(lat * 1000) / 1000; // ~100m precision
    const roundedLng = Math.round(lng * 1000) / 1000;
    return `${this.CACHE_PREFIX}${roundedLat}_${roundedLng}`;
  }

  // Check if cache entry is expired
  private isExpired(timestamp: number): boolean {
    const now = Date.now();
    const expiryTime = this.CACHE_EXPIRY_HOURS * 60 * 60 * 1000;
    return (now - timestamp) > expiryTime;
  }

  // Store address data in cache
  public storeAddressData(
    lat: number, 
    lng: number, 
    addressData: {
      areaName: string;
      areaId: string;
      localityName: string;
      localityId: string;
      existingArea: any | null;
      existingLocalities: any[];
      addressFields: any;
      warehouseId: string;
    }
  ): void {
    if (this.DISABLED) {
      console.log('🛑 Address cache disabled via REACT_APP_DISABLE_ADDRESS_CACHE');
      return;
    }
    try {
      const cacheKey = this.generateCacheKey(lat, lng);
      const cacheData: CachedAddressData = {
        ...addressData,
        timestamp: Date.now(),
        coordinates: [lat, lng]
      };

      localStorage.setItem(cacheKey, JSON.stringify(cacheData));
      console.log(`📦 Address data cached for location: ${lat.toFixed(4)}, ${lng.toFixed(4)}`);
    } catch (error) {
      console.warn('Failed to cache address data:', error);
    }
  }

  // Find cached data within proximity radius
  public findNearbyCache(lat: number, lng: number): CachedAddressData | null {
    if (this.DISABLED) {
      console.log('🛑 Address cache lookup skipped (disabled)');
      return null;
    }
    try {
      const allCacheEntries: CacheEntry[] = [];

      // Scan all cache entries
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (!key || !key.startsWith(this.CACHE_PREFIX)) continue;

        const item = localStorage.getItem(key);
        if (!item) continue;

        try {
          const data: CachedAddressData = JSON.parse(item);
          
          // Check if expired
          if (this.isExpired(data.timestamp)) {
            localStorage.removeItem(key);
            continue;
          }

          // Calculate distance
          const distance = this.calculateDistance(
            lat, lng,
            data.coordinates[0], data.coordinates[1]
          );

          // If within proximity radius, add to candidates
          if (distance <= this.PROXIMITY_RADIUS_METERS) {
            allCacheEntries.push({ key, data, distance });
          }
        } catch (parseError) {
          // Remove corrupted cache entries
          localStorage.removeItem(key);
        }
      }

      // Return closest match
      if (allCacheEntries.length > 0) {
        allCacheEntries.sort((a, b) => a.distance - b.distance);
        const closest = allCacheEntries[0];
        console.log(`🎯 Found cached address data ${closest.distance.toFixed(0)}m away:`, closest.data.areaName, '→', closest.data.localityName);
        return closest.data;
      }

      console.log(`❌ No cached address data found within ${this.PROXIMITY_RADIUS_METERS}m radius`);
      return null;
    } catch (error) {
      console.warn('Error searching cache:', error);
      return null;
    }
  }

  // Validate that cached area/locality IDs still exist in database
  public async validateCachedData(cachedData: CachedAddressData, areas: any[], localities: any[]): Promise<boolean> {
    if (this.DISABLED) {
      return false;
    }
    try {
      // Check if area still exists
      const areaExists = areas.some(area => area.id === cachedData.areaId);
      
      // Check if locality still exists  
      const localityExists = localities.some(locality => locality.id === cachedData.localityId);

      const isValid = areaExists && localityExists;
      
      if (!isValid) {
        console.log('🗑️ Cached data validation failed - removing invalid cache entry');
        this.removeCacheEntry(cachedData.coordinates[0], cachedData.coordinates[1]);
      }

      return isValid;
    } catch (error) {
      console.warn('Error validating cached data:', error);
      return false;
    }
  }

  // Remove specific cache entry
  public removeCacheEntry(lat: number, lng: number): void {
    if (this.DISABLED) {
      return;
    }
    try {
      const cacheKey = this.generateCacheKey(lat, lng);
      localStorage.removeItem(cacheKey);
    } catch (error) {
      console.warn('Error removing cache entry:', error);
    }
  }

  // Clean up expired cache entries
  public cleanupExpiredEntries(): void {
    if (this.DISABLED) {
      return;
    }
    try {
      const keysToRemove: string[] = [];
      
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (!key || !key.startsWith(this.CACHE_PREFIX)) continue;

        const item = localStorage.getItem(key);
        if (!item) continue;

        try {
          const data: CachedAddressData = JSON.parse(item);
          if (this.isExpired(data.timestamp)) {
            keysToRemove.push(key);
          }
        } catch (parseError) {
          keysToRemove.push(key);
        }
      }

      // Remove expired entries
      keysToRemove.forEach(key => localStorage.removeItem(key));
      
      if (keysToRemove.length > 0) {
        console.log(`🧹 Cleaned up ${keysToRemove.length} expired cache entries`);
      }
    } catch (error) {
      console.warn('Error during cache cleanup:', error);
    }
  }

  // Get cache statistics for debugging
  public getCacheStats(): { totalEntries: number; validEntries: number; expiredEntries: number } {
    if (this.DISABLED) {
      return { totalEntries: 0, validEntries: 0, expiredEntries: 0 };
    }
    let totalEntries = 0;
    let validEntries = 0;
    let expiredEntries = 0;

    try {
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (!key || !key.startsWith(this.CACHE_PREFIX)) continue;

        totalEntries++;
        const item = localStorage.getItem(key);
        if (!item) continue;

        try {
          const data: CachedAddressData = JSON.parse(item);
          if (this.isExpired(data.timestamp)) {
            expiredEntries++;
          } else {
            validEntries++;
          }
        } catch (parseError) {
          expiredEntries++;
        }
      }
    } catch (error) {
      console.warn('Error getting cache stats:', error);
    }

    return { totalEntries, validEntries, expiredEntries };
  }
}

// Export singleton instance
export const addressCache = new AddressCache();
